// 人设优化服务 - 根据用户描述优化角色人设
import { RealAIService } from './realAiService.js'
import settingsService from './settingsService.js'

class PersonalityOptimizationService {
  /**
   * 优化角色人设
   * @param {string} name - 角色名称
   * @param {string} description - 用户输入的角色描述
   * @param {string} personality - 用户输入的性格特点
   * @returns {Object} 优化后的人设信息
   */
  async optimizePersonality(name, description, personality) {
    try {
      const languageConfig = settingsService.getLanguageModelConfig()
      
      if (!languageConfig.apiKey || !languageConfig.apiUrl) {
        return this.generateMockOptimization(name, description, personality)
      }

      const prompt = `请根据以下信息，优化并完善一个虚拟角色的详细人设：

角色名称：${name}
角色描述：${description || '未提供'}
性格特点：${personality || '未提供'}

请生成：
1. 优化后的角色描述（100-150字）：更详细、生动、有画面感
2. 详细的性格特点（50-100字）：具体的行为模式、说话风格、兴趣爱好
3. 系统提示词（200-300字）：用于AI对话的详细设定，包括：
   - 角色的基本信息和背景
   - 性格特点和行为模式
   - 说话风格和常用表达
   - 兴趣爱好和话题偏好
   - 与其他角色的关系设定

请以JSON格式返回：
{
  "optimizedDescription": "优化后的角色描述",
  "optimizedPersonality": "详细的性格特点",
  "systemPrompt": "详细的系统提示词",
  "keyTraits": ["特征1", "特征2", "特征3"],
  "interests": ["兴趣1", "兴趣2"],
  "speechStyle": "说话风格描述"
}`

      const response = await RealAIService.callLanguageModel(prompt)
      
      // 尝试解析JSON
      try {
        const jsonMatch = response.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0])
          return {
            optimizedDescription: parsed.optimizedDescription || description,
            optimizedPersonality: parsed.optimizedPersonality || personality,
            systemPrompt: parsed.systemPrompt || this.buildDefaultSystemPrompt(name, parsed.optimizedDescription || description, parsed.optimizedPersonality || personality),
            keyTraits: parsed.keyTraits || [],
            interests: parsed.interests || [],
            speechStyle: parsed.speechStyle || '自然、友好'
          }
        }
      } catch (e) {
        console.warn('解析优化结果JSON失败，使用降级方案')
      }

      // 降级方案：使用AI返回的文本，简单处理
      return {
        optimizedDescription: description || '一个有趣的AI角色',
        optimizedPersonality: personality || '友好、开朗',
        systemPrompt: this.buildDefaultSystemPrompt(name, description, personality),
        keyTraits: [],
        interests: [],
        speechStyle: '自然、友好'
      }
    } catch (error) {
      console.error('❌ 优化人设失败:', error)
      return this.generateMockOptimization(name, description, personality)
    }
  }

  /**
   * 构建默认系统提示词
   */
  buildDefaultSystemPrompt(name, description, personality) {
    return `你是一个名为${name}的虚拟角色。

${description ? `角色背景：${description}` : ''}

性格特点：${personality || '友好、开朗'}

请以${name}的身份和语气回复用户的消息。回复要：
1. 符合角色的性格特点和背景设定
2. 自然、真实，就像真正的朋友在聊天
3. 简短有趣，不要过于冗长
4. 根据对话历史理解上下文，展现角色的记忆和情感
5. 根据与用户的关系（好感度）调整说话方式和亲密程度`
  }

  /**
   * 生成模拟优化结果（降级方案）
   */
  generateMockOptimization(name, description, personality) {
    return {
      optimizedDescription: description || `一个名为${name}的有趣角色`,
      optimizedPersonality: personality || '友好、开朗、有趣',
      systemPrompt: this.buildDefaultSystemPrompt(name, description, personality),
      keyTraits: personality ? personality.split(/[、，,]/).map(t => t.trim()) : ['友好', '开朗'],
      interests: [],
      speechStyle: '自然、友好'
    }
  }

  /**
   * 根据对话表现学习并更新人设
   * @param {Object} character - 角色信息
   * @param {Array} conversationHistory - 对话历史
   * @returns {Object} 更新后的人设信息
   */
  async learnFromConversation(character, conversationHistory) {
    try {
      const languageConfig = settingsService.getLanguageModelConfig()
      
      if (!languageConfig.apiKey || !languageConfig.apiUrl) {
        return character
      }

      // 分析角色在对话中的表现
      const characterMessages = conversationHistory
        .filter(msg => msg.sender === 'character')
        .slice(-20) // 最近20条角色消息

      if (characterMessages.length < 5) {
        return character // 对话太少，不更新
      }

      const prompt = `根据以下对话记录，分析角色"${character.name}"的实际表现，并优化其性格描述：

角色原始设定：
名称：${character.name}
描述：${character.description || '未提供'}
性格：${character.personality || '未提供'}

角色在对话中的实际表现：
${characterMessages.map(msg => `${character.name}：${msg.content}`).join('\n')}

请分析：
1. 角色在实际对话中展现出的性格特点（可能与原始设定有差异）
2. 角色常用的表达方式和说话习惯
3. 角色感兴趣的话题类型
4. 建议更新的性格描述（50-100字）

请以JSON格式返回：
{
  "updatedPersonality": "更新后的性格描述",
  "speechPatterns": ["表达方式1", "表达方式2"],
  "topicPreferences": ["话题1", "话题2"]
}`

      const response = await RealAIService.callLanguageModel(prompt)
      
      try {
        const jsonMatch = response.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0])
          
          return {
            ...character,
            personality: parsed.updatedPersonality || character.personality,
            learnedTraits: {
              speechPatterns: parsed.speechPatterns || [],
              topicPreferences: parsed.topicPreferences || [],
              updatedAt: Date.now()
            }
          }
        }
      } catch (e) {
        console.warn('解析学习结果失败')
      }

      return character
    } catch (error) {
      console.error('❌ 学习人设失败:', error)
      return character
    }
  }
}

export default new PersonalityOptimizationService()

